import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InputField from '../components/InputField';
import InputMask from 'react-input-mask';
import Header from '../others/Header';
import config from '../config/config';
import ConfirmModal from '../others/ConfirmModal';
import { useToast } from '../components/ToastContext';
import UsuariosModal from '../others/UsuariosModal';
import CertificadoNfseModal from '../others/CertificadoNfseModal';
import {
  FaBuilding,
  FaIdCard,
  FaUser,
  FaPhone,
  FaCalendar,
  FaEdit,
  FaTrash,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaCity,
  FaMoneyBillWave,
  FaFileInvoice,
  FaLock
} from 'react-icons/fa';

// ── NFS-e Nacional: opções de regime tributário (Simples Nacional) ─────────
// Espelha o enum OptanteSimplesNacional da lib nfse-nacional-node.
const OPCOES_OPTANTE_SIMPLES_NACIONAL = [
  { value: 1, label: 'Não optante' },
  { value: 2, label: 'Optante - MEI (Microempreendedor Individual)' },
  { value: 3, label: 'Optante - ME/EPP (Microempresa ou Empresa de Pequeno Porte)' }
];

// Espelha o enum RegimeTributacaoSimplesNacional — só é obrigatório quando
// optanteSimplesNacional = 3 (ME/EPP).
const OPCOES_REGIME_TRIBUTACAO_SN = [
  { value: 1, label: 'Tributos federais e municipal pelo SN' },
  { value: 2, label: 'Tributos federais pelo SN e ISSQN pela NFS-e (legislação municipal)' },
  { value: 3, label: 'Tributos federais e municipal pela NFS-e (legislação federal/municipal)' }
];

// Presets comuns de código de tributação nacional (cTribNac) — cobre o caso
// de cartório/serventia. Para outros verticais do Nexus (despachante,
// clínica), adicione o código correspondente aqui conforme for confirmado.
const PRESETS_CTRIBNAC = [
  { value: '210101', label: '210101 — Serviços de registros públicos, cartorários e notariais' },
  { value: '', label: 'Outro (informar manualmente)' }
];

function EmpresaRegistration() {
  const [formData, setFormData] = useState({
    cnpj: '',
    nomeFantasia: '',
    razaoSocial: '',
    ddd: '',
    telefone: '',
    nomeContato: '',
    cpfContato: '',
    tipoJuridico: '',
    periodoDias: 30,
    ativa: true,
    tipoPlano: 'TESTE',
    diaCobranca: 1,
    endereco: '',
    numero: '',
    bairro: '',
    cep: '',
    cidade: '',
    issqn: 0.0,

    // ── Campos novos: NFS-e Nacional ──────────────────────────────────────
    codigoIbgeMunicipio: '',       // 7 dígitos — auto-preenchido pelo CEP (ViaCEP), mas editável
    cTribNac: '210101',            // código de tributação nacional do serviço prestado
    cTribNacPersonalizado: false,  // controla se o select está em modo "outro" (texto livre)
    optanteSimplesNacional: 1,     // 1=Não optante, 2=MEI, 3=ME/EPP
    regimeTributacaoSimplesNacional: '', // só obrigatório quando optanteSimplesNacional=3
    serieDpsNfse: '1'              // série da DPS na NFS-e Nacional
  });

  const [empresas, setEmpresas] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editEmpresaId, setEditEmpresaId] = useState(null);
  const [errors, setErrors] = useState({});
  const { API_BASE_URL } = config;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [empresaEmEdicao, setEmpresaEmEdicao] = useState(null);
  const [isUsuariosModalOpen, setIsUsuariosModalOpen] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [isCertificadoModalOpen, setIsCertificadoModalOpen] = useState(false);
  const [empresaParaCertificado, setEmpresaParaCertificado] = useState(null);

  const handleOpenCertificadoModal = (empresa) => {
    setEmpresaParaCertificado(empresa);
    setIsCertificadoModalOpen(true);
  };

  const handleCloseCertificadoModal = () => {
    setIsCertificadoModalOpen(false);
    setEmpresaParaCertificado(null);
  };

  // ── CORREÇÃO: documentType agora só é usado para exibição final
  // (após o campo perder o foco). Durante a digitação, a máscara é
  // calculada dinamicamente pela quantidade real de dígitos já digitados,
  // nunca travando o campo em 11 dígitos antes da hora.
  const [documentType, setDocumentType] = useState('cnpj');

  const { addToast } = useToast();

  // Função para detectar o tipo de documento baseado no valor
  const detectDocumentType = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      return 'cpf';
    }
    return 'cnpj';
  };

  // ── CORREÇÃO DEFINITIVA: formatação manual do campo CPF/CNPJ ───────────────
  // O componente InputMask trava o usuário em 11 dígitos porque a prop
  // `mask` só é reavaliada DEPOIS de um re-render do React — ou seja, no
  // exato momento em que o usuário digita o 12º dígito, a máscara antiga
  // (de CPF, fixa em 11 posições) ainda está ativa e bloqueia a tecla antes
  // que o componente tenha chance de trocar para a máscara de CNPJ.
  //
  // A solução é formatar o valor manualmente a cada tecla digitada, usando
  // sempre a contagem de dígitos JÁ PRESENTE no evento atual (sem depender
  // de um re-render anterior). Assim a transição de CPF para CNPJ acontece
  // de forma instantânea e sem travar nenhuma tecla.
  const formatarDocumentoDigitando = (valorBruto) => {
    const digitos = valorBruto.replace(/\D/g, '').slice(0, 14); // nunca passa de 14 dígitos (CNPJ)

    if (digitos.length <= 11) {
      // Formata como CPF: 999.999.999-99
      return digitos
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    // Formata como CNPJ: 99.999.999/9999-99
    return digitos
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  // Handler para o campo de documento (CNPJ/CPF) — recebe o valor já
  // formatado manualmente, sem depender de InputMask para este campo.
  const handleDocumentChange = (rawValue) => {
    const formatado = formatarDocumentoDigitando(rawValue);
    handleInputChange('cnpj', formatado);
  };

  // ── Fixa o tipo final apenas quando o campo perde o foco ───────────────────
  // Usado apenas para mensagens/validações posteriores — a formatação em
  // si já foi tratada em tempo real pelo formatarDocumentoDigitando.
  const handleDocumentBlur = () => {
    const tipoFinal = detectDocumentType(formData.cnpj);
    setDocumentType(tipoFinal);
  };

  // Handler único para os campos do formulário
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ── NFS-e Nacional: busca o código IBGE do município automaticamente
  // ao sair do campo CEP, usando o ViaCEP (que já retorna o campo `ibge`
  // com o código de 7 dígitos). O usuário ainda pode editar manualmente
  // depois, caso o ViaCEP não retorne o dado corretamente.
  const handleCepBlur = async () => {
    const cepLimpo = (formData.cep || '').replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setBuscandoCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data && !response.data.erro) {
        setFormData(prev => ({
          ...prev,
          cidade: response.data.localidade || prev.cidade,
          endereco: prev.endereco || response.data.logradouro || '',
          bairro: prev.bairro || response.data.bairro || '',
          codigoIbgeMunicipio: response.data.ibge || prev.codigoIbgeMunicipio
        }));
      } else {
        addToast('error', 'CEP não encontrado. Preencha a cidade e o código IBGE manualmente.');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP no ViaCEP:', error);
      addToast('error', 'Não foi possível consultar o CEP. Preencha a cidade e o código IBGE manualmente.');
    } finally {
      setBuscandoCep(false);
    }
  };

  // Alterna o select de cTribNac entre os presets e o modo "outro" (texto livre)
  const handleCTribNacPresetChange = (value) => {
    if (value === '') {
      setFormData(prev => ({ ...prev, cTribNac: '', cTribNacPersonalizado: true }));
    } else {
      setFormData(prev => ({ ...prev, cTribNac: value, cTribNacPersonalizado: false }));
    }
  };

  const handleDeleteConfirmation = (empresa) => {
    setEmpresaToDelete(empresa);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (empresaToDelete) {
      await handleDelete(empresaToDelete);
      setIsConfirmModalOpen(false);
      setEmpresaToDelete(null);
    }
  };

  const fetchUsuarios = async (empresaId, empresaNome) => {
    const token = localStorage.getItem("jwtToken");
    setUsuariosLoading(true);
    setSelectedEmpresa(empresaNome);

    try {
      const response = await axios.get(`${API_BASE_URL}/usuarios/${empresaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsuarios(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao buscar usuários:', error);
        addToast('error', 'Erro ao carregar usuários. Tente novamente.');
      }
    } finally {
      setUsuariosLoading(false);
    }
  };

  const handleOpenUsuariosModal = (empresa) => {
    setIsUsuariosModalOpen(true);
    fetchUsuarios(empresa.idEmpresa, empresa.nomeFantasia);
  };

  const handleCloseUsuariosModal = () => {
    setIsUsuariosModalOpen(false);
    setUsuarios([]);
    setSelectedEmpresa(null);
  };

  const handleUnauthorized = () => {
    window.location.href = "/login";
  };

  const fetchEmpresas = async () => {
    const token = localStorage.getItem("jwtToken");
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/empresas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setEmpresas(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao buscar empresas:', error);
        addToast('error', 'Erro ao carregar empresas. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      handleUnauthorized();
    } else {
      fetchEmpresas();
    }
  }, []);

  const validateFields = () => {
    const newErrors = {};

    if (!formData.cnpj) newErrors.cnpj = 'CPF/CNPJ é obrigatório';
    if (!formData.nomeFantasia) newErrors.nomeFantasia = 'Nome Fantasia é obrigatório';
    if (!formData.razaoSocial) newErrors.razaoSocial = 'Razão Social é obrigatória';
    if (!formData.ddd) newErrors.ddd = 'DDD é obrigatório';
    if (!formData.telefone) newErrors.telefone = 'Telefone é obrigatório';
    if (!formData.nomeContato) newErrors.nomeContato = 'Nome do Contato é obrigatório';
    if (!formData.tipoJuridico) newErrors.tipoJuridico = 'Tipo Jurídico é obrigatório';
    if (formData.periodoDias <= 0) newErrors.periodoDias = 'Período deve ser maior que zero';
    if (formData.issqn < 0) newErrors.issqn = 'ISSQN não pode ser negativo';

    if (formData.tipoPlano === 'MENSAL' && (formData.diaCobranca < 1 || formData.diaCobranca > 31)) {
      newErrors.diaCobranca = 'Dia da cobrança deve ser entre 1 e 31';
    }

    // ── Validações NFS-e Nacional ──────────────────────────────────────────
    const ibgeLimpo = (formData.codigoIbgeMunicipio || '').replace(/\D/g, '');
    if (!ibgeLimpo) {
      newErrors.codigoIbgeMunicipio = 'Código IBGE do município é obrigatório';
    } else if (ibgeLimpo.length !== 7) {
      newErrors.codigoIbgeMunicipio = 'Código IBGE deve ter exatamente 7 dígitos';
    }

    if (!formData.cTribNac || !formData.cTribNac.trim()) {
      newErrors.cTribNac = 'Código de tributação nacional (cTribNac) é obrigatório';
    }

    if (!formData.optanteSimplesNacional) {
      newErrors.optanteSimplesNacional = 'Regime tributário (Simples Nacional) é obrigatório';
    }

    if (Number(formData.optanteSimplesNacional) === 3 && !formData.regimeTributacaoSimplesNacional) {
      newErrors.regimeTributacaoSimplesNacional = 'Obrigatório para optantes ME/EPP';
    }

    if (!formData.serieDpsNfse || !formData.serieDpsNfse.trim()) {
      newErrors.serieDpsNfse = 'Série da DPS é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateFields()) return;

    const empresaData = {
      cnpj: formData.cnpj.replace(/\D/g, ''),
      nomeFantasia: formData.nomeFantasia,
      razaoSocial: formData.razaoSocial,
      ddd: formData.ddd,
      telefone: formData.telefone,
      nomeContato: formData.nomeContato,
      cpfContato: formData.cpfContato.replace(/\D/g, ''),
      tipoJuridico: formData.tipoJuridico,
      periodoDias: formData.periodoDias,
      ativa: formData.ativa,
      tipoPlano: formData.tipoPlano,
      diaCobranca: formData.tipoPlano === 'MENSAL' ? formData.diaCobranca : null,
      endereco: formData.endereco,
      numero: formData.numero,
      bairro: formData.bairro,
      cep: formData.cep.replace(/\D/g, ''),
      cidade: formData.cidade,
      issqn: formData.issqn,

      // ── Campos novos: NFS-e Nacional ────────────────────────────────────
      codigoIbgeMunicipio: (formData.codigoIbgeMunicipio || '').replace(/\D/g, ''),
      cTribNac: formData.cTribNac.trim(),
      optanteSimplesNacional: Number(formData.optanteSimplesNacional),
      regimeTributacaoSimplesNacional:
        Number(formData.optanteSimplesNacional) === 3 && formData.regimeTributacaoSimplesNacional
          ? Number(formData.regimeTributacaoSimplesNacional)
          : null,
      serieDpsNfse: formData.serieDpsNfse.trim()
    };

    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);

      if (isEditing) {
        await axios.put(`${API_BASE_URL}/empresas/${editEmpresaId}`, empresaData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setIsEditing(false);
        setEditEmpresaId(null);
      } else {
        await axios.post(`${API_BASE_URL}/empresas`, empresaData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      await fetchEmpresas();
      resetForm();
      addToast('success', isEditing ? 'Empresa atualizada com sucesso!' : 'Empresa cadastrada com sucesso!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao registrar ou editar empresa:', error);
        addToast('error', 'Erro ao salvar empresa. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cnpj: '',
      nomeFantasia: '',
      razaoSocial: '',
      ddd: '',
      telefone: '',
      nomeContato: '',
      cpfContato: '',
      tipoJuridico: '',
      periodoDias: 30,
      ativa: true,
      tipoPlano: 'TESTE',
      diaCobranca: 1,
      endereco: '',
      numero: '',
      bairro: '',
      cep: '',
      cidade: '',
      issqn: 0.0,
      codigoIbgeMunicipio: '',
      cTribNac: '210101',
      cTribNacPersonalizado: false,
      optanteSimplesNacional: 1,
      regimeTributacaoSimplesNacional: '',
      serieDpsNfse: '1'
    });
    setDocumentType('cnpj');
    setErrors({});
  };

  const handleEdit = (empresa) => {
    const docValue = empresa.cnpj || '';
    const docType = detectDocumentType(docValue);
    setDocumentType(docType);

    const cTribNacValor = empresa.cTribNac || '210101';
    const ehPreset = PRESETS_CTRIBNAC.some(p => p.value === cTribNacValor && p.value !== '');

    setFormData({
      // CORREÇÃO: formata o documento vindo do backend (que chega só com
      // dígitos) para exibição correta no campo (com pontos/barra/hífen).
      cnpj: formatarDocumentoDigitando(docValue),
      nomeFantasia: empresa.nomeFantasia || '',
      razaoSocial: empresa.razaoSocial || '',
      ddd: empresa.ddd || '',
      telefone: empresa.telefone || '',
      nomeContato: empresa.nomeContato || '',
      cpfContato: empresa.cpfContato || '',
      tipoJuridico: empresa.tipoJuridico || '',
      periodoDias: empresa.periodoDias || 30,
      ativa: empresa.ativa !== undefined ? empresa.ativa : true,
      tipoPlano: empresa.tipoPlano || 'TESTE',
      diaCobranca: empresa.diaCobranca || 1,
      endereco: empresa.endereco || '',
      numero: empresa.numero || '',
      bairro: empresa.bairro || '',
      cep: empresa.cep || '',
      cidade: empresa.cidade || '',
      issqn: empresa.issqn || 0.0,

      codigoIbgeMunicipio: empresa.codigoIbgeMunicipio || '',
      cTribNac: cTribNacValor,
      cTribNacPersonalizado: !ehPreset,
      optanteSimplesNacional: empresa.optanteSimplesNacional || 1,
      regimeTributacaoSimplesNacional: empresa.regimeTributacaoSimplesNacional || '',
      serieDpsNfse: empresa.serieDpsNfse || '1'
    });

    setIsEditing(true);
    setEditEmpresaId(empresa.idEmpresa);
    setEmpresaEmEdicao(empresa);

    document.getElementById('form-empresa').scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (empresa) => {
    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);

      await axios.delete(`${API_BASE_URL}/empresas/${empresa.idEmpresa}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      await fetchEmpresas();
      addToast('success', 'Empresa excluída com sucesso!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao excluir empresa:', error);
        addToast('error', 'Erro ao excluir empresa. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditEmpresaId(null);
    setEmpresaEmEdicao(null);
    resetForm();
  };

  // Funções de formatação
  const formatarDocumento = (doc) => {
    if (!doc) return 'Não informado';
    const docLimpo = doc.replace(/\D/g, '');
    if (docLimpo.length === 11) {
      return docLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (docLimpo.length === 14) {
      return docLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return 'Não informado';
    const telefoneLimpo = telefone.replace(/\D/g, '');
    if (telefoneLimpo.length === 9) {
      return `${telefoneLimpo.substring(0, 5)}-${telefoneLimpo.substring(5)}`;
    } else if (telefoneLimpo.length === 8) {
      return `${telefoneLimpo.substring(0, 4)}-${telefoneLimpo.substring(4)}`;
    }
    return telefone;
  };

  const formatarCPF = (cpf) => {
    if (!cpf) return 'Não informado';
    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length === 11) {
      return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };

  const formatarCEP = (cep) => {
    if (!cep) return 'Não informado';
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      return `${cepLimpo.substring(0, 5)}-${cepLimpo.substring(5)}`;
    }
    return cep;
  };

  const formatarTipoPlano = (tipoPlano) => {
    const tipos = {
      'TESTE': 'Teste',
      'DEFINITIVO': 'Definitivo',
      'MENSAL': 'Mensal'
    };
    return tipos[tipoPlano] || tipoPlano;
  };

  const formatarOptanteSimplesNacional = (valor) => {
    const opcao = OPCOES_OPTANTE_SIMPLES_NACIONAL.find(o => o.value === Number(valor));
    return opcao ? opcao.label : 'Não informado';
  };

  const getStatusEmpresa = (empresa) => {
    if (!empresa.ativa) return { status: 'Inativa', cor: '#dc3545', icone: FaTimesCircle };

    if (!empresa.dataInicioPeriodo) {
      return { status: 'Ativa (não iniciado)', cor: '#17a2b8', icone: FaInfoCircle };
    }

    const hoje = new Date();
    const fim = empresa.dataFimPeriodo ? new Date(empresa.dataFimPeriodo) : null;

    if (fim && hoje > fim) return { status: 'Período expirado', cor: '#ffc107', icone: FaExclamationTriangle };

    return { status: 'Ativa', cor: '#28a745', icone: FaCheckCircle };
  };

  const getDiasRestantes = (empresa) => {
    if (!empresa.ativa || !empresa.dataFimPeriodo) return '-';

    const hoje = new Date();
    const fim = new Date(empresa.dataFimPeriodo);

    if (hoje > fim) return 'Expirado';

    const diffTime = fim - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return `${diffDays} dias`;
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'Não definida';
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };

  const formatarMoeda = (valor) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Componente Card para cada empresa
  const EmpresaCard = ({ empresa }) => {
    const statusInfo = getStatusEmpresa(empresa);
    const StatusIcon = statusInfo.icone;

    // Emissão de NFS-e habilitada: fica numa entidade separada
    // (EmpresaNfseConfig, não vem junto do GET /empresas), então cada card
    // busca o próprio estado ao montar. Tela exclusiva MANAGER — nem o
    // menu nem os endpoints GET/PATCH /empresas/{id}/nfse-habilitada
    // deixam GERENTE/COLABORADOR chegar aqui (ver EmpresaController).
    const [nfseHabilitada, setNfseHabilitada] = useState(null); // null = carregando
    const [salvandoNfseHabilitada, setSalvandoNfseHabilitada] = useState(false);

    useEffect(() => {
      let cancelado = false;
      const carregarNfseHabilitada = async () => {
        try {
          const token = localStorage.getItem('jwtToken');
          const response = await axios.get(
            `${API_BASE_URL}/empresas/${empresa.idEmpresa}/nfse-habilitada`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!cancelado) setNfseHabilitada(!!response.data);
        } catch (error) {
          if (!cancelado) setNfseHabilitada(false);
        }
      };
      carregarNfseHabilitada();
      return () => { cancelado = true; };
    }, [empresa.idEmpresa]);

    const alternarNfseHabilitada = async () => {
      const novoValor = !nfseHabilitada;
      setSalvandoNfseHabilitada(true);
      try {
        const token = localStorage.getItem('jwtToken');
        await axios.patch(
          `${API_BASE_URL}/empresas/${empresa.idEmpresa}/nfse-habilitada`,
          null,
          { params: { habilitada: novoValor }, headers: { Authorization: `Bearer ${token}` } }
        );
        setNfseHabilitada(novoValor);
        addToast('success', `Emissão de NFS-e ${novoValor ? 'habilitada' : 'desabilitada'} para ${empresa.nomeFantasia}.`);
      } catch (error) {
        addToast('error', 'Erro ao atualizar emissão de NFS-e: ' + (error.response?.data?.message || error.message));
      } finally {
        setSalvandoNfseHabilitada(false);
      }
    };

    return (
      <div style={cardStyles.card}>
        {/* Header do Card */}
        <div style={cardStyles.cardHeader}>
          <div style={cardStyles.empresaInfo}>
            <FaBuilding style={cardStyles.empresaIcon} />
            <div>
              <h3 style={cardStyles.empresaNome}>{empresa.nomeFantasia}</h3>
              <p style={cardStyles.razaoSocial}>{empresa.razaoSocial}</p>
            </div>
          </div>
          <div style={{...cardStyles.status, backgroundColor: statusInfo.cor}}>
            <StatusIcon style={cardStyles.statusIcon} />
            <span>{statusInfo.status}</span>
          </div>
        </div>

        {/* Informações da Empresa */}
        <div style={cardStyles.cardBody}>
          <div style={cardStyles.infoGrid}>
            <div style={cardStyles.infoItem}>
              <FaIdCard style={cardStyles.infoIcon} />
              <div>
                <label>Documento</label>
                <span>{formatarDocumento(empresa.cnpj)}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaUser style={cardStyles.infoIcon} />
              <div>
                <label>Contato</label>
                <span>{empresa.nomeContato || 'Não informado'}</span>
              </div>
            </div>

            {empresa.cpfContato && (
              <div style={cardStyles.infoItem}>
                <FaIdCard style={cardStyles.infoIcon} />
                <div>
                  <label>CPF Contato</label>
                  <span>{formatarCPF(empresa.cpfContato)}</span>
                </div>
              </div>
            )}

            <div style={cardStyles.infoItem}>
              <FaPhone style={cardStyles.infoIcon} />
              <div>
                <label>Telefone</label>
                <span>{empresa.ddd ? `(${empresa.ddd}) ` : ''}{formatarTelefone(empresa.telefone)}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaCalendar style={cardStyles.infoIcon} />
              <div>
                <label>Plano</label>
                <span>{formatarTipoPlano(empresa.tipoPlano)}</span>
              </div>
            </div>

            {empresa.issqn !== undefined && empresa.issqn > 0 && (
              <div style={cardStyles.infoItem}>
                <FaMoneyBillWave style={cardStyles.infoIcon} />
                <div>
                  <label>ISSQN</label>
                  <span>{formatarMoeda(empresa.issqn)}</span>
                </div>
              </div>
            )}

            {empresa.codigoIbgeMunicipio && (
              <div style={cardStyles.infoItem}>
                <FaFileInvoice style={cardStyles.infoIcon} />
                <div>
                  <label>NFS-e — Município (IBGE)</label>
                  <span>{empresa.codigoIbgeMunicipio}</span>
                </div>
              </div>
            )}

            {empresa.cTribNac && (
              <div style={cardStyles.infoItem}>
                <FaFileInvoice style={cardStyles.infoIcon} />
                <div>
                  <label>NFS-e — cTribNac</label>
                  <span>{empresa.cTribNac}</span>
                </div>
              </div>
            )}

            {empresa.optanteSimplesNacional && (
              <div style={cardStyles.infoItem}>
                <FaFileInvoice style={cardStyles.infoIcon} />
                <div>
                  <label>Regime Tributário</label>
                  <span>{formatarOptanteSimplesNacional(empresa.optanteSimplesNacional)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Endereço */}
          {(empresa.endereco || empresa.numero || empresa.bairro || empresa.cidade || empresa.cep) && (
            <div style={cardStyles.enderecoInfo}>
              <FaMapMarkerAlt style={cardStyles.enderecoIcon} />
              <div>
                <label>Endereço</label>
                <span>
                  {empresa.endereco && `${empresa.endereco}, `}
                  {empresa.numero && `${empresa.numero}`}
                  {empresa.bairro && ` - ${empresa.bairro}`}
                  {empresa.cidade && `, ${empresa.cidade}`}
                  {empresa.cep && ` - CEP: ${formatarCEP(empresa.cep)}`}
                </span>
              </div>
            </div>
          )}

          {/* Informações do Plano */}
          <div style={cardStyles.planoInfo}>
            <div style={cardStyles.planoItem}>
              <strong>Período:</strong> {empresa.periodoDias || 30} dias
            </div>
            {empresa.tipoPlano === 'MENSAL' && empresa.diaCobranca && (
              <div style={cardStyles.planoItem}>
                <strong>Dia cobrança:</strong> {empresa.diaCobranca}
              </div>
            )}
            <div style={cardStyles.planoItem}>
              <strong>Dias restantes:</strong> {getDiasRestantes(empresa)}
            </div>
          </div>

          {/* Datas do Período */}
          {(empresa.dataInicioPeriodo || empresa.dataFimPeriodo) && (
            <div style={cardStyles.periodoInfo}>
              {empresa.dataInicioPeriodo && (
                <div style={cardStyles.periodoItem}>
                  <strong>Início:</strong> {formatarData(empresa.dataInicioPeriodo)}
                </div>
              )}
              {empresa.dataFimPeriodo && (
                <div style={cardStyles.periodoItem}>
                  <strong>Fim:</strong> {formatarData(empresa.dataFimPeriodo)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Emissão de NFS-e habilitada - exclusivo MANAGER */}
        <div style={cardStyles.nfseHabilitadaRow}>
          <span style={cardStyles.nfseHabilitadaLabel}>
            <FaFileInvoice style={{ marginRight: 6 }} />
            Emissão de NFS-e
          </span>
          <button
            onClick={alternarNfseHabilitada}
            disabled={nfseHabilitada === null || salvandoNfseHabilitada}
            style={{
              ...cardStyles.nfseHabilitadaToggle,
              ...(nfseHabilitada ? cardStyles.nfseHabilitadaToggleOn : cardStyles.nfseHabilitadaToggleOff),
              opacity: (nfseHabilitada === null || salvandoNfseHabilitada) ? 0.6 : 1
            }}
            title={nfseHabilitada ? 'Clique para desabilitar a emissão de NFS-e' : 'Clique para habilitar a emissão de NFS-e'}
          >
            {nfseHabilitada === null ? 'Carregando...' : (nfseHabilitada ? 'Habilitada' : 'Desabilitada')}
          </button>
        </div>

        {/* Ações do Card */}
        <div style={cardStyles.cardActions}>
          <button
            onClick={() => handleOpenCertificadoModal(empresa)}
            style={cardStyles.certificadoButton}
            title="Certificado NFS-e"
          >
            <FaLock />
          </button>
          <button
            onClick={() => handleEdit(empresa)}
            style={cardStyles.editButton}
            title="Editar empresa"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleOpenUsuariosModal(empresa)}
            style={cardStyles.usersButton}
            title="Ver usuários"
          >
            <FaUsers />
          </button>
          <button
            onClick={() => handleDeleteConfirmation(empresa)}
            style={cardStyles.deleteButton}
            title="Excluir empresa"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  const inputFieldStyle = (fieldName) => ({
    ...styles.inputField,
    borderColor: errors[fieldName] ? '#dc3545' : '#e9ecef',
    opacity: loading ? 0.7 : 1
  });

  return (
    <div>
      <Header />
      <div style={styles.container}>
        {/* Formulário */}
        <div id="form-empresa">
          <h2 style={styles.header}>
            {isEditing ? 'Editar Empresa' : 'Cadastro de Empresa'}
          </h2>
          <h4 style={styles.subHeader}>
            Preencha os dados abaixo para {isEditing ? 'editar' : 'cadastrar'} a empresa
          </h4>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Linha 1 - Documento e Nomes */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>CPF/CNPJ *</label>
                {/* ── CORREÇÃO: input controlado manualmente, sem InputMask.
                    A formatação (formatarDocumentoDigitando) é aplicada a
                    cada tecla com base na contagem real de dígitos já
                    digitados naquele evento — isso evita a trava de 1 tecla
                    que ocorria com InputMask ao tentar passar de 11 para
                    12 dígitos (transição de CPF para CNPJ). ── */}
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.cnpj}
                  onChange={(e) => handleDocumentChange(e.target.value)}
                  onBlur={handleDocumentBlur}
                  disabled={loading}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  style={inputFieldStyle('cnpj')}
                />
                {errors.cnpj && <span style={styles.errorText}>{errors.cnpj}</span>}
                <small style={styles.hintText}>
                  Digite 11 dígitos para CPF (ex: titular de cartório) ou 14 para CNPJ — a máscara se ajusta automaticamente.
                  Este mesmo documento é usado como Prestador/Emitente na NFS-e Nacional.
                </small>
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nome Fantasia *</label>
                <InputField
                  value={formData.nomeFantasia}
                  onChange={(e) => handleInputChange('nomeFantasia', e.target.value)}
                  placeholder="Nome Fantasia da Empresa"
                  disabled={loading}
                  style={inputFieldStyle('nomeFantasia')}
                />
                {errors.nomeFantasia && <span style={styles.errorText}>{errors.nomeFantasia}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Razão Social *</label>
                <InputField
                  value={formData.razaoSocial}
                  onChange={(e) => handleInputChange('razaoSocial', e.target.value)}
                  placeholder="Razão Social da Empresa"
                  disabled={loading}
                  style={inputFieldStyle('razaoSocial')}
                />
                {errors.razaoSocial && <span style={styles.errorText}>{errors.razaoSocial}</span>}
              </div>
            </div>

            {/* Linha 2 - Telefone e Contatos */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>DDD *</label>
                <InputField
                  value={formData.ddd}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,3}$/.test(value)) {
                      handleInputChange('ddd', value);
                    }
                  }}
                  placeholder="DDD"
                  disabled={loading}
                  style={inputFieldStyle('ddd')}
                />
                {errors.ddd && <span style={styles.errorText}>{errors.ddd}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Telefone *</label>
                <InputField
                  value={formData.telefone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,9}$/.test(value)) {
                      handleInputChange('telefone', value);
                    }
                  }}
                  placeholder="999999999"
                  disabled={loading}
                  style={inputFieldStyle('telefone')}
                />
                {errors.telefone && <span style={styles.errorText}>{errors.telefone}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nome do Contato *</label>
                <InputField
                  value={formData.nomeContato}
                  onChange={(e) => handleInputChange('nomeContato', e.target.value)}
                  placeholder="Nome da pessoa de contato"
                  disabled={loading}
                  style={inputFieldStyle('nomeContato')}
                />
                {errors.nomeContato && <span style={styles.errorText}>{errors.nomeContato}</span>}
              </div>
            </div>

            {/* Linha 3 - CPF Contato e Tipo Jurídico */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>CPF do Contato</label>
                <InputMask
                  mask="999.999.999-99"
                  value={formData.cpfContato}
                  onChange={(e) => handleInputChange('cpfContato', e.target.value)}
                  disabled={loading}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="text"
                      placeholder="000.000.000-00"
                      style={inputFieldStyle('cpfContato')}
                    />
                  )}
                </InputMask>
                {errors.cpfContato && <span style={styles.errorText}>{errors.cpfContato}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo Jurídico *</label>
                <select
                  value={formData.tipoJuridico}
                  onChange={(e) => handleInputChange('tipoJuridico', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoJuridico')}
                >
                  <option value="">Selecione o Tipo Jurídico</option>
                  <option value="SA">Sociedade Anônima (SA)</option>
                  <option value="LTDA">Sociedade Limitada (LTDA)</option>
                  <option value="EIRELI">Empresa Individual de Responsabilidade Limitada (EIRELI)</option>
                  <option value="MEI">Microempreendedor Individual (MEI)</option>
                  <option value="OUTROS">Outros</option>
                </select>
                {errors.tipoJuridico && <span style={styles.errorText}>{errors.tipoJuridico}</span>}
              </div>
            </div>

            {/* Linha 4 - Endereço */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Endereço</label>
                <InputField
                  value={formData.endereco}
                  onChange={(e) => handleInputChange('endereco', e.target.value)}
                  placeholder="Nome da rua, avenida..."
                  disabled={loading}
                  style={inputFieldStyle('endereco')}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Número</label>
                <InputField
                  value={formData.numero}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  placeholder="Número"
                  disabled={loading}
                  style={inputFieldStyle('numero')}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Bairro</label>
                <InputField
                  value={formData.bairro}
                  onChange={(e) => handleInputChange('bairro', e.target.value)}
                  placeholder="Bairro"
                  disabled={loading}
                  style={inputFieldStyle('bairro')}
                />
              </div>
            </div>

            {/* Linha 5 - CEP, Cidade e ISSQN */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>CEP</label>
                <InputMask
                  mask="99999-999"
                  value={formData.cep}
                  onChange={(e) => handleInputChange('cep', e.target.value)}
                  onBlur={handleCepBlur}
                  disabled={loading}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="text"
                      placeholder="00000-000"
                      style={inputFieldStyle('cep')}
                    />
                  )}
                </InputMask>
                {buscandoCep && <small style={styles.hintText}>Buscando cidade e código IBGE...</small>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Cidade</label>
                <InputField
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  placeholder="Cidade"
                  disabled={loading}
                  style={inputFieldStyle('cidade')}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>ISSQN (%)</label>
                <InputField
                  value={formData.issqn}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    handleInputChange('issqn', isNaN(value) ? 0 : value);
                  }}
                  placeholder="0,00"
                  type="number"
                  step="0.01"
                  disabled={loading}
                  style={inputFieldStyle('issqn')}
                />
                {errors.issqn && <span style={styles.errorText}>{errors.issqn}</span>}
              </div>
            </div>

            {/* Linha 6 - NFS-e Nacional: Município IBGE, cTribNac e Série DPS */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Código IBGE do Município *</label>
                <InputField
                  value={formData.codigoIbgeMunicipio}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                    handleInputChange('codigoIbgeMunicipio', value);
                  }}
                  placeholder="Ex: 4304507 (preenchido automaticamente pelo CEP)"
                  disabled={loading}
                  style={inputFieldStyle('codigoIbgeMunicipio')}
                />
                {errors.codigoIbgeMunicipio && <span style={styles.errorText}>{errors.codigoIbgeMunicipio}</span>}
                <small style={styles.hintText}>7 dígitos — usado na emissão e prestação do serviço na NFS-e Nacional</small>
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Serviço Prestado (cTribNac) *</label>
                <select
                  value={formData.cTribNacPersonalizado ? '' : formData.cTribNac}
                  onChange={(e) => handleCTribNacPresetChange(e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('cTribNac')}
                >
                  {PRESETS_CTRIBNAC.map(opt => (
                    <option key={opt.value || 'outro'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {formData.cTribNacPersonalizado && (
                  <InputField
                    value={formData.cTribNac}
                    onChange={(e) => handleInputChange('cTribNac', e.target.value)}
                    placeholder="Digite o código de tributação nacional (cTribNac)"
                    disabled={loading}
                    style={{ ...inputFieldStyle('cTribNac'), marginTop: '8px' }}
                  />
                )}
                {errors.cTribNac && <span style={styles.errorText}>{errors.cTribNac}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Série da DPS *</label>
                <InputField
                  value={formData.serieDpsNfse}
                  onChange={(e) => handleInputChange('serieDpsNfse', e.target.value)}
                  placeholder="1"
                  disabled={loading}
                  style={inputFieldStyle('serieDpsNfse')}
                />
                {errors.serieDpsNfse && <span style={styles.errorText}>{errors.serieDpsNfse}</span>}
              </div>
            </div>

            {/* Linha 7 - NFS-e Nacional: Regime Tributário */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Regime Tributário (Simples Nacional) *</label>
                <select
                  value={formData.optanteSimplesNacional}
                  onChange={(e) => handleInputChange('optanteSimplesNacional', Number(e.target.value))}
                  disabled={loading}
                  style={inputFieldStyle('optanteSimplesNacional')}
                >
                  {OPCOES_OPTANTE_SIMPLES_NACIONAL.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.optanteSimplesNacional && <span style={styles.errorText}>{errors.optanteSimplesNacional}</span>}
                <small style={styles.hintText}>Titular de cartório pessoa física costuma ser "Não optante" — confirme com o contador</small>
              </div>

              {Number(formData.optanteSimplesNacional) === 3 && (
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>Regime de Apuração (ME/EPP) *</label>
                  <select
                    value={formData.regimeTributacaoSimplesNacional}
                    onChange={(e) => handleInputChange('regimeTributacaoSimplesNacional', e.target.value)}
                    disabled={loading}
                    style={inputFieldStyle('regimeTributacaoSimplesNacional')}
                  >
                    <option value="">Selecione</option>
                    {OPCOES_REGIME_TRIBUTACAO_SN.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {errors.regimeTributacaoSimplesNacional && (
                    <span style={styles.errorText}>{errors.regimeTributacaoSimplesNacional}</span>
                  )}
                </div>
              )}
            </div>

            {/* Linha 8 - Plano e Status */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo de Plano *</label>
                <select
                  value={formData.tipoPlano}
                  onChange={(e) => handleInputChange('tipoPlano', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoPlano')}
                >
                  <option value="TESTE">Teste</option>
                  <option value="DEFINITIVO">Definitivo</option>
                  <option value="MENSAL">Mensal</option>
                </select>
              </div>

              {formData.tipoPlano === 'MENSAL' && (
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>Dia da Cobrança *</label>
                  <select
                    value={formData.diaCobranca}
                    onChange={(e) => handleInputChange('diaCobranca', parseInt(e.target.value) || 1)}
                    disabled={loading}
                    style={inputFieldStyle('diaCobranca')}
                  >
                    <option value="">Selecione o dia</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                      <option key={dia} value={dia}>{dia}</option>
                    ))}
                  </select>
                  {errors.diaCobranca && <span style={styles.errorText}>{errors.diaCobranca}</span>}
                </div>
              )}

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Período de Acesso (dias)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.periodoDias}
                  onChange={(e) => handleInputChange('periodoDias', parseInt(e.target.value) || 30)}
                  disabled={loading}
                  style={inputFieldStyle('periodoDias')}
                  placeholder="30"
                />
                {errors.periodoDias && <span style={styles.errorText}>{errors.periodoDias}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Status</label>
                <select
                  value={formData.ativa}
                  onChange={(e) => handleInputChange('ativa', e.target.value === 'true')}
                  disabled={loading}
                  style={inputFieldStyle('ativa')}
                >
                  <option value={true}>Ativa</option>
                  <option value={false}>Inativa</option>
                </select>
              </div>
            </div>

            {/* Botões */}
            <div style={styles.buttonContainer}>
              <button 
                style={{ 
                  ...styles.button, 
                  backgroundColor: isEditing ? '#2196F3' : '#4CAF50',
                  opacity: loading ? 0.7 : 1
                }} 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Salvando...' : (isEditing ? 'Atualizar Empresa' : 'Cadastrar Empresa')}
              </button>
              
              {isEditing && (
                <>
                  <button 
                    style={{ 
                      ...styles.button, 
                      backgroundColor: '#f44336', 
                      marginLeft: '10px',
                      opacity: loading ? 0.7 : 1
                    }} 
                    onClick={handleCancelEdit}
                    type="button"
                    disabled={loading}
                  >
                    Cancelar Edição
                  </button>
                  
                  {empresaEmEdicao && (
                    <button 
                      type="button"
                      onClick={() => handleOpenUsuariosModal(empresaEmEdicao)}
                      style={styles.usuariosButton}
                    >
                      👥 Ver Usuários
                    </button>
                  )}
                </>
              )}
            </div>
          </form>
        </div>

        {/* Cards das Empresas */}
        <div style={styles.cardsSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.header}>Empresas Cadastradas</h2>
            <div style={styles.cardsInfo}>
              <span style={styles.cardsCount}>{empresas.length} empresa(s) cadastrada(s)</span>
            </div>
          </div>
          
          {loading ? (
            <div style={styles.loading}>Carregando empresas...</div>
          ) : empresas.length === 0 ? (
            <div style={styles.emptyState}>
              <FaBuilding style={styles.emptyIcon} />
              <h3>Nenhuma empresa cadastrada</h3>
              <p>Cadastre a primeira empresa usando o formulário acima</p>
            </div>
          ) : (
            <div style={styles.cardsGrid}>
              {empresas.map(empresa => (
                <EmpresaCard key={empresa.idEmpresa} empresa={empresa} />
              ))}
            </div>
          )}
        </div>

        {/* Modais */}
        <UsuariosModal
          isOpen={isUsuariosModalOpen}
          onClose={handleCloseUsuariosModal}
          usuarios={usuarios}
          empresaNome={selectedEmpresa}
          loading={usuariosLoading}
        />

        <CertificadoNfseModal
          isOpen={isCertificadoModalOpen}
          onClose={handleCloseCertificadoModal}
          empresa={empresaParaCertificado}
        />

        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmModalOpen(false)}
          message={`Tem certeza que deseja excluir a empresa "${empresaToDelete?.nomeFantasia}"?`}
        />
      </div>
    </div>
  );
}

// Estilos para os Cards
const cardStyles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    border: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    borderBottom: '2px solid #f8f9fa',
    paddingBottom: '15px',
  },
  empresaInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  empresaIcon: {
    fontSize: '24px',
    color: '#6f42c1',
    marginTop: '2px',
  },
  empresaNome: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
  },
  razaoSocial: {
    margin: '5px 0 0 0',
    fontSize: '14px',
    color: '#666',
    fontStyle: 'italic',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  statusIcon: {
    fontSize: '12px',
  },
  cardBody: {
    marginBottom: '15px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '15px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  infoIcon: {
    fontSize: '16px',
    color: '#6f42c1',
    minWidth: '16px',
  },
  enderecoInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '15px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  enderecoIcon: {
    fontSize: '16px',
    color: '#6f42c1',
    marginTop: '2px',
  },
  planoInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '10px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  planoItem: {
    fontSize: '14px',
    color: '#333',
  },
  periodoInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    padding: '10px',
    backgroundColor: '#e7f3ff',
    borderRadius: '6px',
    border: '1px solid #b3d9ff',
  },
  periodoItem: {
    fontSize: '13px',
    color: '#0066cc',
  },
  nfseHabilitadaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    marginTop: '10px',
    borderTop: '1px solid #e9ecef',
  },
  nfseHabilitadaLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
  },
  nfseHabilitadaToggle: {
    padding: '6px 14px',
    border: 'none',
    borderRadius: '14px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
  },
  nfseHabilitadaToggleOn: {
    backgroundColor: '#d4edda',
    color: '#1e7e34',
  },
  nfseHabilitadaToggleOff: {
    backgroundColor: '#f1f1f1',
    color: '#6c757d',
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '15px',
    borderTop: '1px solid #e9ecef',
  },
  certificadoButton: {
    padding: '8px 12px',
    backgroundColor: '#343a40',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  editButton: {
    padding: '8px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  usersButton: {
    padding: '8px 12px',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  deleteButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
};

// Estilos principais
const styles = {
  container: {
    padding: '20px',
    width: '95%',
    maxWidth: '1400px',
    margin: 'auto',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  header: {
    color: '#333',
    marginBottom: '10px',
    fontSize: '24px',
    fontWeight: '600',
  },
  subHeader: {
    color: '#666',
    marginBottom: '20px',
    fontSize: '16px',
    fontWeight: 'normal',
  },
  form: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  inputGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '25px',
  },
  fieldContainer: {
    flex: '1 1 calc(33.333% - 20px)',
    minWidth: '280px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
  },
  inputField: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    backgroundColor: '#fff',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '5px',
    display: 'block',
  },
  hintText: {
    color: '#6c757d',
    fontSize: '11px',
    marginTop: '4px',
    display: 'block',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '15px',
    marginTop: '30px',
    flexWrap: 'wrap',
  },
  button: {
    padding: '12px 25px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    minWidth: '160px',
  },
  usuariosButton: {
    padding: '12px 20px',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  cardsSection: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  cardsInfo: {
    color: '#666',
    fontSize: '14px',
  },
  cardsCount: {
    backgroundColor: '#e7f3ff',
    padding: '8px 15px',
    borderRadius: '20px',
    fontWeight: '600',
    color: '#0066cc',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    fontSize: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#dee2e6',
    marginBottom: '15px',
  },
};

export default EmpresaRegistration;
